import { engineComponent, ArrayUtils } from "neo-common";
import { BaseElement } from "./BaseElement";
import { ContainerStyle } from "../Styles/ContainerStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { CanvasManager } from "../../Managers/CanvasManager";
import { HtmlManager } from "../../Managers/HtmlManager";
import { ComponentTypes } from "../../ComponentTypes";
import { Tweens } from "../../Effects/Tweens";
import { Attributes } from "../../../Utils/Attributes";
import { ShapeElement } from "./ShapeElement";

/**
 * ContainerElement class is class which creates holder for other elements.
 * Container element can be used for grouping more display objects and better organization in xmls.
 * Can be defined also by Style with class ContainerStyle.
 * Example 1:
 *   <ContainerElement Class="ContainerElement" X="0" Y="0">
 *       <Child1 />
 *       <Child2 />
 *       ...
 *       <ChildX />
 *   </ContainerElement>
 *
 * Example 2:
 *   <ContainerElement Class="ContainerElement" Style="ContainerStyle" />
 *
 * @class ContainerElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ContainerElement extends BaseElement {
    // style is optional in this element
    style: ContainerStyle;
    // list of children
    childs: Object;

    _tint: number;

    constructor() {
        super();
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs.hasOwnProperty(key)) {
                    if (this.childs[key] instanceof PIXI.DisplayObject) {
                        this.childs[key].destroy(options);
                    }
                    this.childs[key] = null;
                }
            }

            this.childs = null;
        }
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element): void {
        super.createByNode(node);

        /*
         * Style can be define for better usage of container elements.
         * For example when you want to use same display objects with same names on more than one places.
         * Style for container element has to be instance of ContainerStyle.
         * @property Style
         * @type string
         * @default null
         */
        if (node.hasAttribute(ContainerElementAttributes.STYLE)) {
            // get style from StyleManager
            this.style = <ContainerStyle>StyleManager.Styles[node.getAttribute(ContainerElementAttributes.STYLE)];
            if (this.style != null) {
                // parse childs from style
                if (this.childs == null) {
                    this.childs = CanvasManager.parseElements(this.style.node, this);
                    HtmlManager.parseElements(this.style.node, null);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name " + node.getAttribute(ContainerElementAttributes.STYLE));
                }
            }
        } else {
            // parse childs from node
            if (this.childs == null) {
                this.childs = CanvasManager.parseElements(node, this);
                HtmlManager.parseElements(node, null);
            }
        }

        if (this.childs == null) {
            if (DEBUG) {
                this.log("WARNING container element with name " + node.nodeName + " has no children");
            }
        }
    }

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(ContainerElementAttributes.TINT)) {
            this.setTint(parseInt(node.getAttribute(ContainerElementAttributes.TINT), 16));
        }

        this.runTintTween(node);

        let child = node.firstElementChild;
        while (child) {
            if (child.tagName) {
                if (this.childs[child.tagName]) {
                    this.childs[child.tagName].updateByNode(child);
                }
            }
            child = child.nextElementSibling;
        }
    }

    public setTint(tintColor: number): void {
        if (this._tint === tintColor || isNaN(tintColor)) {
            return;
        }
        this._tint = tintColor;
        for (let child in this.childs) {
            if (typeof this.childs[child].tint === "number") {
                this.childs[child].tint = tintColor;
            } else if (typeof this.childs[child].setTint === "function") {
                this.childs[child].setTint(tintColor);
            }
        }
    }

    set tint(tintColor: number) {
        this.setTint(tintColor);
    }

    get tint(): number {
        return this._tint;
    }

    public runTintTween(node: Element): boolean {
        if (!node.hasAttribute(ContainerElementAttributes.TINT_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "tint");

        let tintTween: Array<number>;

        let tintTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ContainerElementAttributes.TINT_TWEEN_POSITION,
            null,
        );
        if (tintTweenPosition == ContainerElementAttributes.TINT_TWEEN_POSITION_VALUE.SMOOTH) {
            tintTween = node
                .getAttribute(ContainerElementAttributes.TINT_TWEEN)
                .split(";")
                .map((value) => parseInt(value, 16));
            if (tintTween.length) {
                tintTween[0] = this.tint;
            }
        } else {
            tintTween = node
                .getAttribute(ContainerElementAttributes.TINT_TWEEN)
                .split(";")
                .map((value) => parseInt(value, 16));
        }

        const tweenValues: Array<number> = tintTween || [];

        const tween: string = "tint";
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ContainerElementAttributes.TINT_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ContainerElementAttributes.TINT_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(ContainerElementAttributes.TINT_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ContainerElementAttributes.TINT_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ContainerElementAttributes.TINT_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ContainerElementAttributes.TINT_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ContainerElementAttributes.TINT_TWEEN_KEY_TIMES))
            : null;

        Tweens.colorTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    public getChildElementByName(name: string): Object {
        if (this.childs) {
            if (this.childs[name]) {
                return this.childs[name];
            }
        }

        return null;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ContainerElement
     */
    clone(): ContainerElement {
        let element: ContainerElement = new ContainerElement();

        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.name = this.name;

        element.childs = {};
        element.removeChildren();

        for (let key in this.childs) {
            element.childs[key] = this.childs[key].clone();
            element.addChild(element.childs[key]);
        }

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        if (this.maskGraphics != null) {
            let elementMask = <ShapeElement>this.maskGraphics.clone();
            elementMask.position.x = this.maskGraphics.position.x;
            elementMask.position.y = this.maskGraphics.position.y;

            element.maskGraphics = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

const ContainerElementAttributes = {
    DEBUG: "Debug",
    /**
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    STYLE: "Style",

    TINT: "Tint",

    TINT_TWEEN: "TintTween",
    TINT_TWEEN_EASE: "TintTweenTease",
    TINT_TWEEN_DURATION: "TintTweenDuration",
    TINT_TWEEN_DELAY: "TintTweenDelay",
    TINT_TWEEN_POSITION: "TintTweenPosition",
    TINT_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        SMOOTH: "smooth",
    },
    TINT_TWEEN_LOOP: "TintTweenLoop",
    TINT_TWEEN_KEY_TIMES: "TintTweenKeyTimes",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ContainerElement.ts
 **/